import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  TrendingUp, TrendingDown, Shield, AlertCircle, AlertTriangle, Clock, Euro,
  FileText, RefreshCw, BarChart3, ChevronRight, FileDown, Percent, Target
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ComposedChart, Line, CartesianGrid, Area
} from 'recharts'
import { useAuth } from '../../../contexts/AuthContext'
import { toast } from 'sonner'
import TransportBudgetPanel from '../../../components/conservation/TransportBudgetPanel'

const API_URL = process.env.REACT_APP_BACKEND_URL

function fmt(n, currency = true) {
  if (n == null || n === '') return '\u2014'
  const num = Number(n)
  if (isNaN(num)) return '\u2014'
  if (currency) return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(num)
  return new Intl.NumberFormat('fr-FR').format(num)
}

function KpiCard({ label, value, sub, icon: Icon, accentBg, accentText, onClick }) {
  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-5 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accentBg} ${accentText}`}>
          <Icon size={15} />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  )
}

function SectionCard({ title, children, action, actionLabel }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
        {action && (
          <button onClick={action} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
            {actionLabel} <ChevronRight size={12} />
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <strong>{p.name.includes('(') || p.name.includes('Prov') ? fmt(p.value) : p.value}</strong>
        </p>
      ))}
    </div>
  )
}

// ── S/P color logic based on sp_cible ──
function spColor(sp, cible) {
  if (!cible || cible <= 0) return 'text-slate-800'
  return sp > cible ? 'text-red-600' : 'text-emerald-600'
}
function spBg(sp, cible) {
  if (!cible || cible <= 0) return 'bg-slate-50'
  return sp > cible ? 'bg-red-50' : 'bg-emerald-50'
}

// ══════════════════════════════════════════════════════════════════════════════
// DAF DASHBOARD — Police selector + S/P cible + simplified
// ══════════════════════════════════════════════════════════════════════════════

export default function DAFDashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const tenantId = profile?.tenant_id

  const [polices, setPolices] = useState([])
  const [selectedPoliceId, setSelectedPoliceId] = useState('')
  const [data, setData] = useState(null)
  const [spData, setSpData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [annee, setAnnee] = useState(null)
  const [exercicesDispo, setExercicesDispo] = useState([])

  useEffect(() => {
    if (!tenantId) return
    fetch(`${API_URL}/api/conservation/${tenantId}/exercices`)
      .then(r => r.json())
      .then(d => { setExercicesDispo(d.exercices || []); setAnnee(d.annee_courante || new Date().getFullYear()) })
      .catch(() => setAnnee(new Date().getFullYear()))
  }, [tenantId])

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)

  // Load polices list
  useEffect(() => {
    if (!tenantId) return
    fetch(`${API_URL}/api/polices?tenant_id=${tenantId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const list = (d?.polices || []).filter(p => p.statut !== 'resilie')
        setPolices(list)
        if (list.length === 1) setSelectedPoliceId(list[0].id)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [tenantId])

  // Load dashboard data when police selected
  const load = useCallback(async () => {
    if (!tenantId || !selectedPoliceId || !annee) return
    setLoading(true)
    try {
      const [dashRes, spRes] = await Promise.all([
        fetch(`${API_URL}/api/daf/dashboard?tenant_id=${tenantId}&annee=${annee}`),
        fetch(`${API_URL}/api/budget/sp-complet/${tenantId}?police_id=${selectedPoliceId}`),
      ])
      if (dashRes.ok) setData(await dashRes.json())
      if (spRes.ok) setSpData(await spRes.json())
    } catch {
      toast.error('Erreur chargement')
    } finally {
      setLoading(false)
    }
  }, [tenantId, selectedPoliceId, annee])

  useEffect(() => { if (selectedPoliceId) load() }, [load, selectedPoliceId])

  // ── No police selected : show selector ──
  if (!selectedPoliceId) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pilotage Budget & S/P</h1>
          <p className="text-sm text-slate-500 mt-1">Selectionnez votre contrat pour acceder au tableau de bord</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : polices.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucun contrat actif</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="police-selector">
            {polices.map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedPoliceId(p.id)}
                className="text-left bg-white rounded-xl border border-slate-200 p-5 hover:border-emerald-400 hover:shadow-md transition-all group"
                data-testid={`select-police-${p.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-800 group-hover:text-emerald-700">{p.libelle || p.numero_police}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{p.numero_police}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {p.type_police} &mdash; {p.date_effet?.slice(0, 10)} &rarr; {p.date_echeance?.slice(0, 10)}
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-slate-300 group-hover:text-emerald-500 mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── Police selected : show dashboard ──
  if (loading || !data || !spData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    )
  }

  const { kpi, evolution_mensuelle, budgets, exercice_label,
          conservation_detail, synthese_amiable } = data

  const selectedPolice = polices.find(p => p.id === selectedPoliceId)
  const isFlotte = (selectedPolice?.type_police || sp.type_police || '') === 'flotte'
  const sp = spData.sp_par_police?.[0] || {}
  const spCible = spData.sp_cible ?? sp.sp_cible

  const hasTransport = (polices || []).some(p => ['rc_transport', 'ad_valorem'].includes(p.type_police))
  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto" data-testid="daf-dashboard">
      {hasTransport && <TransportBudgetPanel tenantId={profile.tenant_id} userId={profile.id} />}

      {/* Header with police selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setSelectedPoliceId(''); setSpData(null) }}
            className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
            data-testid="btn-back-polices"
          >
            &larr; Changer de contrat
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{selectedPolice?.libelle || sp.libelle}</h1>
            <p className="text-xs text-slate-400">
              {sp.numero_police} &mdash; Exercice {sp.exercice_label || exercice_label} &mdash; {sp.jours_ecoules}/{sp.jours_periode}j
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={annee || ''}
            onChange={e => setAnnee(Number(e.target.value))}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2"
            data-testid="select-annee"
          >
            {exercicesDispo.length > 0
              ? exercicesDispo.map(ex => <option key={ex.annee} value={ex.annee}>Exercice {ex.label}{ex.en_cours ? ' (en cours)' : ex.futur ? ' (à venir)' : ''}</option>)
              : years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 text-sm font-medium" data-testid="btn-refresh">
            <RefreshCw size={14} /> Actualiser
          </button>
        </div>
      </div>

      {/* ── S/P Cible + KPIs principaux ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-grid">
        <KpiCard label="Prime HT" value={fmt(sp.prime_ht)} sub={sp.source_prime || ''} icon={Euro} accentBg="bg-blue-50" accentText="text-blue-600" />
        {sp.franchises_recuperees > 0 ? (
          <>
            <KpiCard label="Charge nette compagnie" value={fmt(sp.charge_sinistres)} sub={`brute ${fmt(sp.charge_brute)} — ${sp.nb_sinistres} sinistre(s)`} icon={AlertCircle} accentBg="bg-amber-50" accentText="text-amber-600" />
            <KpiCard label="Franchises à charge client" value={fmt(sp.franchises_recuperees)} sub="budget de conservation" icon={BarChart3} accentBg="bg-rose-50" accentText="text-rose-600" />
          </>
        ) : (
          <>
            <KpiCard label="Charge sinistres" value={fmt(sp.charge_sinistres)} sub={`${sp.nb_sinistres} sinistre(s)`} icon={AlertCircle} accentBg="bg-amber-50" accentText="text-amber-600" />
            <KpiCard label="Provisions" value={fmt(sp.provisions)} icon={BarChart3} accentBg="bg-slate-50" accentText="text-slate-600" />
          </>
        )}
        <div className={`rounded-xl border border-slate-200 p-5 shadow-sm ${spBg(sp.sp_reel, spCible)}`} data-testid="kpi-sp-reel">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">S/P reel</p>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${spBg(sp.sp_reel, spCible)} ${spColor(sp.sp_reel, spCible)}`}>
              <Percent size={15} />
            </div>
          </div>
          <p className={`text-2xl font-bold tabular-nums ${spColor(sp.sp_reel, spCible)}`}>{sp.sp_reel?.toFixed(1)}%</p>
          <p className="text-xs text-slate-400 mt-1">Charge / Prime HT</p>
        </div>
        <div className={`rounded-xl border border-slate-200 p-5 shadow-sm ${spBg(sp.sp_annualise, spCible)}`} data-testid="kpi-sp-annualise">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">S/P annualise</p>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${spBg(sp.sp_annualise, spCible)} ${spColor(sp.sp_annualise, spCible)}`}>
              <TrendingUp size={15} />
            </div>
          </div>
          <p className={`text-2xl font-bold tabular-nums ${spColor(sp.sp_annualise, spCible)}`}>{sp.sp_annualise?.toFixed(1)}%</p>
          <p className="text-xs text-slate-400 mt-1">Projection exercice</p>
        </div>
        {/* S/P Cible */}
        <div className="bg-white rounded-xl border-2 border-dashed border-slate-300 p-5 shadow-sm" data-testid="kpi-sp-cible">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">S/P cible</p>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600">
              <Target size={15} />
            </div>
          </div>
          <p className="text-2xl font-bold text-indigo-700 tabular-nums">
            {spCible ? `${spCible}%` : '\u2014'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {spCible ? 'Defini par le gestionnaire' : 'Non defini'}
          </p>
        </div>
      </div>

      {/* S/P Annualise vs Cible — visual indicator bar */}
      {spCible > 0 && sp.prime_ht > 0 && (() => {
        const maxScale = Math.max(spCible * 1.4, sp.sp_annualise * 1.2, 100)
        const ciblePct = (spCible / maxScale) * 100
        const cursorPct = (sp.sp_annualise / maxScale) * 100
        return (
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="sp-cible-gauge">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 text-sm">Positionnement S/P annualise vs cible</h3>
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${sp.sp_annualise <= spCible ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {sp.sp_annualise <= spCible ? 'En dessous du seuil' : 'Au-dessus du seuil'}
            </span>
          </div>
          <div className="relative h-8 bg-slate-100 rounded-full overflow-hidden">
            {/* Green zone (0 to cible) */}
            <div className="absolute inset-y-0 left-0 bg-emerald-100 rounded-l-full" style={{ width: `${ciblePct}%` }} />
            {/* Red zone (cible to end) */}
            <div className="absolute inset-y-0 bg-red-100 rounded-r-full" style={{ left: `${ciblePct}%`, right: 0 }} />
            {/* Cible marker line */}
            <div className="absolute inset-y-0 w-0.5 bg-indigo-600 z-10" style={{ left: `${ciblePct}%` }}>
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-indigo-600 whitespace-nowrap">
                Cible {spCible}%
              </div>
            </div>
            {/* Current S/P annualise cursor */}
            <div
              className={`absolute top-1 bottom-1 w-3 rounded-full z-20 ${sp.sp_annualise <= spCible ? 'bg-emerald-500' : 'bg-red-500'}`}
              style={{ left: `${Math.min(cursorPct, 98)}%`, transform: 'translateX(-50%)' }}
            >
              <div className={`absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-bold whitespace-nowrap ${sp.sp_annualise <= spCible ? 'text-emerald-600' : 'text-red-600'}`}>
                {sp.sp_annualise?.toFixed(1)}%
              </div>
            </div>
          </div>
          <div className="flex justify-between mt-6 text-[10px] text-slate-400">
            <span>0%</span>
            <span>Temporalite : {sp.jours_ecoules}j ecoules / {sp.jours_periode}j — {sp.jours_periode - sp.jours_ecoules}j restants</span>
            <span>{Math.round(maxScale)}%</span>
          </div>
        </div>
        )
      })()}

      {/* Sinistres ouverts + clos + cout — filtered by police via spData */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Sinistres" value={sp.nb_sinistres || 0} sub={`${sp.nb_sinistres_exclus || 0} exclus (amiable/retention)`} icon={FileText} accentBg="bg-slate-50" accentText="text-slate-600" />
        <KpiCard label={sp.franchises_recuperees > 0 ? 'Charge nette compagnie' : 'Charge sinistres'} value={fmt(sp.charge_sinistres)} sub={sp.franchises_recuperees > 0 ? 'provision − franchises client' : 'provision compagnie'} icon={Euro} accentBg="bg-amber-50" accentText="text-amber-600" />
        <KpiCard label="Jours ecoules" value={`${sp.jours_ecoules || 0}j / ${sp.jours_periode || 365}j`} sub={`${(sp.jours_periode || 365) - (sp.jours_ecoules || 0)}j restants`} icon={Clock} accentBg="bg-blue-50" accentText="text-blue-600" />
        <KpiCard label="Prime proratisee" value={fmt(sp.prime_proratisee)} sub={`sur ${sp.jours_ecoules || 0} jours`} icon={BarChart3} accentBg="bg-purple-50" accentText="text-purple-600" />
      </div>

      {/* Evolution mensuelle — only if police has sinistres */}
      {(sp.nb_sinistres > 0) && (
        <SectionCard title={`Evolution mensuelle \u2014 exercice ${sp.exercice_label || exercice_label || annee}`}>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={evolution_mensuelle} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={v => `${Math.round(v / 1000)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar yAxisId="left" dataKey="nouveaux" name="Nouveaux" fill="#3B82F6" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="left" dataKey="clos" name="Clos" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="cout" name="Cout" stroke="#F59E0B" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </SectionCard>
      )}

      {/* Budgets (conservation + amiable) — flotte uniquement */}
      {isFlotte && (budgets.amiable.alloue > 0 || budgets.conservation.alloue > 0 || (synthese_amiable && synthese_amiable.nb_amiables > 0)) && (
        <SectionCard title="Suivi budgetaire">
          <div className="space-y-3">
            {budgets.conservation.alloue > 0 && (
              <BudgetBar label="Budget de conservation" consomme={budgets.conservation.consomme} alloue={budgets.conservation.alloue} pct={budgets.conservation.pct} colorClass="bg-blue-500" />
            )}
            {budgets.amiable.alloue > 0 && (
              <BudgetBar label="Budget règlement amiable (coût HT réglé)" consomme={budgets.amiable.consomme} alloue={budgets.amiable.alloue} pct={budgets.amiable.pct} colorClass="bg-emerald-500" />
            )}
            {synthese_amiable && synthese_amiable.nb_amiables > 0 && (
              <div className="pt-2 border-t border-slate-100 grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-amber-50 rounded-lg p-3" data-testid="kpi-charge-optimisee">
                  <p className="text-[10px] text-amber-600 uppercase tracking-wide mb-0.5">Charge sinistre optimisée</p>
                  <p className="text-lg font-bold text-amber-700">{fmt(synthese_amiable.charge_sinistre_optimisee)}</p>
                  <p className="text-[10px] text-amber-500">charge évitée à la compagnie ({synthese_amiable.nb_irsa || 0} IRSA × 2 030 €{synthese_amiable.nb_irca ? ` + ${synthese_amiable.nb_irca} IRCA × 1 600 €` : ''}{synthese_amiable.nb_droit_commun ? ` + ${synthese_amiable.nb_droit_commun} DC` : ''})</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-[10px] text-emerald-600 uppercase tracking-wide mb-0.5">Economies amiables</p>
                  <p className="text-lg font-bold text-emerald-700">{fmt(synthese_amiable.total_economie_amiable)}</p>
                  <p className="text-[10px] text-emerald-500">{synthese_amiable.nb_amiables} dossier(s)</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Cout amiable reel</p>
                  <p className="text-lg font-bold text-slate-700">{fmt(synthese_amiable.total_cout_amiable)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-[10px] text-blue-500 uppercase tracking-wide mb-0.5">Repartition</p>
                  <p className="text-xs text-slate-600 mt-1">
                    <span className="font-semibold text-blue-700">{synthese_amiable.nb_assurance}</span> assurance
                    {' / '}
                    <span className="font-semibold text-emerald-700">{synthese_amiable.nb_amiables}</span> amiable
                  </p>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Conservation detail (flotte uniquement) */}
      {isFlotte && conservation_detail && (
        <SectionCard title="Budget de Conservation (Flotte Auto)">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Budget initial</p>
              <p className="text-lg font-bold text-slate-800">{fmt(conservation_detail.montant_initial)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Consomme</p>
              <p className="text-lg font-bold text-red-600">{fmt(conservation_detail.consomme)}</p>
              <p className="text-[10px] text-slate-400">{conservation_detail.nb_sinistres_consommes} sinistre(s)</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Restant</p>
              <p className={`text-lg font-bold ${conservation_detail.reste < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(conservation_detail.reste)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Taux consommation</p>
              <p className={`text-lg font-bold ${conservation_detail.taux_consommation > 80 ? 'text-red-600' : conservation_detail.taux_consommation > 50 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {conservation_detail.taux_consommation}%
              </p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Analyse Parc Assure */}
      <ParcAnalyticsTabDAF tenantId={tenantId} annee={annee} />

      {/* Footer */}
      <div className="text-xs text-slate-400 text-center pb-2">
        Donnees mises a jour en temps reel &mdash; Propriete de C2 Assurances
      </div>
    </div>
  )
}


// ── Budget Bar Component ──
function BudgetBar({ label, consomme, alloue, pct, colorClass }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <span className="text-xs text-slate-500">{fmt(consomme)} / {fmt(alloue)} ({pct}%)</span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2">
        <div className={`h-2 rounded-full transition-all ${colorClass}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  )
}


// ── Parc Analytics Tab DAF ──
function ParcAnalyticsTabDAF({ tenantId, annee }) {
  const [open, setOpen] = useState(false)
  const [vehData, setVehData] = useState(null)
  const [siteData, setSiteData] = useState(null)

  useEffect(() => {
    if (!tenantId || !open) return
    fetch(`${API_URL}/api/tracker/accidentologie/${tenantId}${annee ? `?annee=${annee}` : ''}`)
      .then(r => r.ok ? r.json() : null).then(d => d && setVehData(d)).catch(() => {})
    fetch(`${API_URL}/api/tracker/sinistralite-sites/${tenantId}${annee ? `?annee=${annee}` : ''}`)
      .then(r => r.ok ? r.json() : null).then(d => d && setSiteData(d)).catch(() => {})
  }, [tenantId, annee, open])

  const hasData = (vehData?.nb_vehicules_sinistres > 0) || (siteData?.nb_sites_sinistres > 0) || (vehData?.total_sinistres > 0) || (siteData?.total_sinistres > 0)

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="parc-analytics-tab-daf">
      <div onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 cursor-pointer" data-testid="parc-analytics-toggle">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-emerald-600" />
          <h3 className="font-semibold text-slate-800 text-base">Analyse Parc Assure</h3>
        </div>
        <ChevronRight size={16} className={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </div>
      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 pt-4 space-y-5">
          {!vehData && !siteData ? (
            <div className="flex justify-center py-4"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : !hasData ? (
            <div className="text-center py-6">
              <Shield className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">Aucune donnee de parc assure disponible</p>
            </div>
          ) : (
            <>
              {vehData && vehData.total_sinistres > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-3">
                    Vehicules &mdash; {vehData.total_sinistres || vehData.nb_vehicules_sinistres} sinistres sur {vehData.nb_vehicules_sinistres} vehicules / {vehData.nb_vehicules_total} au parc
                  </p>
                  {vehData.top_vehicules?.length > 0 && (
                    <div className="space-y-1">
                      {vehData.top_vehicules.slice(0, 5).map((v, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                          <span className="text-xs font-bold text-slate-800">{v.immatriculation} {v.marque ? `\u2014 ${v.marque}` : ''}</span>
                          <span className="text-xs text-slate-600">{v.nb_sinistres} sin. &mdash; {fmt(v.cout_total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {siteData && siteData.total_sinistres > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-3">
                    Sites &mdash; {siteData.total_sinistres || siteData.nb_sites_sinistres} sinistres sur {siteData.nb_sites_sinistres} sites / {siteData.nb_sites_total} sites au parc
                  </p>
                  {siteData.top_sites?.length > 0 && (
                    <div className="space-y-1">
                      {siteData.top_sites.slice(0, 5).map((s, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                          <span className="text-xs font-semibold text-slate-800">{s.nom} {s.ville ? `\u2014 ${s.ville}` : ''}</span>
                          <span className="text-xs text-slate-600">{s.nb_sinistres} sin. &mdash; {fmt(s.cout_total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
